import React, { useCallback, useEffect, useRef, useState } from "react";
import { FormikProps } from "formik";
import { fromPromise } from "mobx-utils";
import classes from "./MicrosoftDynamicsLoginForm.module.scss";
import parentClasses from "../../MicrosoftDynamicsLogin.module.scss";
import classNames from "classnames";
import * as Yup from "yup";
import { Button } from "@whatslly/ui-components/v2/Button";
import { postJSON } from "@whatslly/commons/apiUtils";
import { TERMS_CONDITIONS_TUVIS_URL, PRIVACY_POLICY_TUVIS_URL } from "@whatslly/commons/constants";
import { useDependencyInjector } from "@whatslly/commons/di";
import globalStyles from "@whatslly/ui-components/v2/globalStyles";
import { useTranslation } from "react-i18next";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { observer, useLocalObservable } from "mobx-react-lite";
import { AppIds } from "@whatslly/types/LoginProviderType";
import { FormElement } from "@whatslly/ui-components/v2/FormElements/Form";
import { FormTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput";
import type { ButtonProps } from "@whatslly/types/UI-V2/PageBuilderTypes";
export type MicrosoftDynamicsLoginFormSettingsType = {
  customDomain?: string;
  secondButton?: ButtonProps;
  hideInputs?: boolean;
  callToActionText?: string;
  connectButtonText?: string;
  hideFooter?: boolean;
  message?: React.ReactNode;
  connectButtonClassName?: string;
};

const MicrosoftDynamicsLoginForm: React.FC<{
  onLogin?: (navigateUrl?: string, state?: string[]) => void;
  customSettings?: MicrosoftDynamicsLoginFormSettingsType;
}> = observer(({ customSettings, onLogin }) => {
  const userStore = useDependencyInjector<IUserStore>(InjectUserStore);
  const [showUserEmail, setShowUserEmail] = useState(false);
  const formikRef = useRef<FormikProps<Record<"email" | "customDomain", string>> | null>(null);
  const [firstLoaded, setFirstLoaded] = useState(false);
  const [hideInputs, setHideInputs] = useState(customSettings?.hideInputs);
  const { t } = useTranslation();

  const store = useLocalObservable(() => ({
    _login: undefined as Promise<void> | undefined,
    get login() {
      if (this._login) {
        return fromPromise(this._login);
      }
    },
    get isLoading() {
      return this.login?.state === "pending";
    },
    get error() {
      if (this.login?.state !== "rejected") {
        return undefined;
      }
      if (this.login?.value && typeof this.login.value === "object" && (this.login.value as any).message) {
        return (this.login.value as any).message;
      }
      return (this.login?.value as any)?.toString();
    },
    submit: async ({ email, customDomain, showUserEmail }: { email: string; customDomain: string; showUserEmail: boolean }) => {
      if (store.login?.state === "pending") {
        return;
      }

      if (showUserEmail) {
        const emailError = await (async () => {
          return await MicrosoftDynamicsLoginFormSchema()
            .validate({
              email,
            })
            .catch((e) => {
              return e.errors;
            });
        })();

        if (Array.isArray(emailError)) {
          formikRef.current?.setFieldError("email", emailError[0]);
          return;
        }
      }

      store._login = userStore
        .login(
          {
            app: AppIds.MICROSOFT_DYNAMICS,
            customDomain: customDomain,
            email,
          },
          t
        )
        .then((result) => {
          onLogin?.(result?.navigate, result?.state);
        });
    },
  }));

  const MicrosoftDynamicsLoginFormSchema = useCallback(() => {
    return Yup.object().shape({
      email: Yup.string()
        .email(t("auth.invalid_email"))
        .test(`Email`, t("auth.invalid_email"), async (val: string | undefined) => {
          const urlSuffix = `/sfdc/v2/auth/validate`;
          const res = await postJSON({
            urlSuffix,
            pod: import.meta.env.VITE_API_DEFAULT_POD?.toString() ?? `us1`,
            body: JSON.stringify({
              username: val || "",
              loginProvider: AppIds.MICROSOFT_DYNAMICS,
              domain: formikRef.current?.values.customDomain,
            }),
          });
          const json = await res.json();
          return await json?.data?.authenticated;
        }),
    });
  }, [t]);

  const { error, isLoading, submit } = store;

  const validate = useCallback(
    async (values: Record<string, any>) => {
      const {
        email,
        customDomain,
      }: {
        email?: string;
        customDomain?: string;
      } = values;
      const errors: Record<string, string> = {};

      if (customDomain === "") {
        errors.customDomain = t("auth.required");
      }

      if (showUserEmail && email === "") {
        errors.email = t("auth.required");
      }
      return errors;
    },
    [showUserEmail, t]
  );

  useEffect(() => {
    if (!firstLoaded) {
      return;
    }
    if (!showUserEmail) {
      formikRef.current?.setFieldValue("email", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showUserEmail]);

  useEffect(() => {
    setFirstLoaded(true);
  }, []);

  return (
    <FormElement
      formikRef={formikRef}
      initialValues={{
        email: "",
        customDomain: customSettings?.customDomain || "",
      }}
      validate={validate}
      onSubmit={({ email, customDomain }) => {
        submit({ email, customDomain, showUserEmail });
      }}
      className={classes.MicrosoftDynamicsLoginForm}
    >
      {!hideInputs && (
        <div className={classes.InputsContainer}>
          <FormTextInput
            type="text"
            data-testid="dynamics-login-domain"
            name={`customDomain`}
            marginBottom={"small"}
            placeholder={`https://*.dynamics.com/*`}
            className={classNames(classes.CustomDomain)}
            paddingInline={`none`}
          />
          {showUserEmail && (
            <FormTextInput
              type={`email`}
              name={`email`}
              marginBottom={"small"}
              data-testid={`dynamics-login-email`}
              placeholder={`Microsoft Dynamics username (name@company.com)`}
              paddingInline={`none`}
            />
          )}
        </div>
      )}
      {customSettings?.message && customSettings.message}
      <div
        className={classNames(classes.needMicrosoftDynamicsUserNameContainer, parentClasses.needUser, {
          [classes.callToAction]: customSettings?.callToActionText,
        })}
      >
        {!customSettings?.callToActionText ? "Need to enter your Microsoft Dynamics username?" : customSettings.callToActionText}{" "}
        <span
          onClick={() => {
            if (customSettings?.hideInputs) {
              setHideInputs((prev) => !prev);
              setShowUserEmail(true);
              return;
            }
            setShowUserEmail(!showUserEmail);
          }}
        >
          click here
        </span>
      </div>
      {error && (
        <div className={classes.ConnectErrorMessage} data-testid="dynamics-login-error">
          {error}
        </div>
      )}
      <div className={classes.ButtonsContainer}>
        <Button
          className={classNames(classes.Submit, customSettings?.connectButtonClassName)}
          text={customSettings?.connectButtonText ? customSettings.connectButtonText : `Connect`}
          disabled={store.login?.state === "pending" || isLoading}
          data-testid={`dynamics-login-submit`}
          type="submit"
          isSubmitting={isLoading}
        />
        {customSettings?.secondButton && <Button type="button" {...customSettings.secondButton} />}
      </div>

      {!customSettings?.hideFooter && (
        <div className={classNames(globalStyles["color-neutral-primary"], classes.termsAndPrivacy)}>
          {`${t("terms_privacy.processing_agree_tuvis")} `}
          <br />
          <a target="_blank" href={TERMS_CONDITIONS_TUVIS_URL} className={classNames(globalStyles["color-neutral-primary"])}>
            {t("terms_privacy.terms_of_service")}
          </a>{" "}
          {`${t("terms_privacy.and")} `}
          <a target="_blank" href={PRIVACY_POLICY_TUVIS_URL} className={classNames(globalStyles["color-neutral-primary"])}>
            {t("terms_privacy.privacy_policy")}
          </a>
        </div>
      )}
    </FormElement>
  );
});

export default MicrosoftDynamicsLoginForm;
